/**
 * Anzeigeformatierungen für DB-Bezeichner.
 * Wandelt subTypes und Item-Keys in lesbare deutsche Namen um.
 */

import { extractQualityTier, getCleanItemName } from './item-badges.js';

// ─── Lager- und Versteck-Namen ────────────────────────────────────────────────

const WAREHOUSE_NAME_MAP: Readonly<Record<string, string>> = {
  // Lager
  garagenbox:      'Garagenbox',
  kellerraum:      'Kellerraum',
  kuehlcontainer:  'Kühlcontainer',
  lagerhalle:      'Lagerhalle',
  industrielager:  'Industrielager',
  containerhof:    'Containerhof',
  grosslager:      'Großlager',
  notfallgarage:   'Notfallgarage',
  org_lager:       'Org-Lager',
  // Verstecke
  dachboden:       'Dachboden',
  hinterzimmer:    'Hinterzimmer',
  wohnung:         'Wohnung',
  kellerstudio:    'Kellerstudio',
  gartenlaube:     'Gartenlaube',
  campingwagen:    'Campingwagen',
  bungalow:        'Bungalow',
  luxus_apartment: 'Luxus-Apartment',
  lagercontainer:  'Lagercontainer',
  bootshaus:       'Bootshaus',
  bunker:          'Bunker',
  // NT-HIDEOUT-PROFILE-01
  waschkeller:        'Waschkeller',
  kirchturm:          'Kirchturm',
  tiefgaragenabteil:  'Tiefgaragenabteil',
  schrebergarten:     'Schrebergarten',
  doenerbude:         'Dönerbude',
  kegelbahn:          'Kegelbahn',
  tankstelle:         'Tankstelle',
  sparkassenfiliale:  'Sparkassenfiliale',
};

// ─── Preis-Formatierung ───────────────────────────────────────────────────────

/**
 * Formatiert Cent-Betrag als deutschen Währungsstring mit 2 Nachkommastellen.
 * Beispiel: 42050 → "420,50 ₦"  |  1234567 → "12.345,67 ₦"
 */
export function formatNox(cents: number | string | bigint): string {
  const n = typeof cents === 'bigint' ? Number(cents) : Number(cents);
  return (n / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₦';
}

/**
 * Formatiert Cent-Betrag als deutschen S-₦-String mit 2 Nachkommastellen.
 * Beispiel: 42050 → "420,50 S-₦"
 */
export function formatBlackMoney(cents: number | string | bigint): string {
  const n = typeof cents === 'bigint' ? Number(cents) : Number(cents);
  return (n / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' S-₦';
}

/**
 * Wandelt DB-Bezeichner (subType, label) in lesbare deutsche Namen um.
 * Beispiel: "luxus_apartment" → "Luxus-Apartment"
 */
export function formatWarehouseName(rawName: string): string {
  if (!rawName) return rawName;
  const key = rawName.toLowerCase();
  const mapped = WAREHOUSE_NAME_MAP[key];
  if (mapped !== undefined) return mapped;
  // Fallback: snake_case → Title Case
  return rawName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// ─── Item-Keys ────────────────────────────────────────────────────────────────

const ITEM_KEY_MAP: Readonly<Record<string, string>> = {
  // Fahrzeuge
  fahrrad_oma:        'Omas Fahrrad',
  ebike:              'E-Bike (mit Anhänger)',
  pkw_rostig_polo:    'Rostiger Polo (Bj. 97)',
  roller_pizza:       'Pizzalieferdienst-Roller',
  golf_geliehen:      'Geliehener Golf',
  taxi_ausser_dienst: 'Taxe (außer Dienst)',
  kombi_getarnt:      'Familien-Kombi (getarnt)',
  eiswagen:           'Eiswagen (klingelt nicht mehr)',
  wurstmobil:         'Wurstmobil (Imbisswagen)',
  dhl_transporter:    'DHL-Transporter (gefälscht)',
  polizei_bus:        'Polizei-Bus',
  lkw_kuehlung:       'Gekühlter LKW',
  // NT-FAHRZEUGE-01
  klapprad:                'Klapprad',
  mofa:                    'Mofa',
  bollerwagen:             'Bollerwagen',
  lastenrad:               'Lastenrad',
  kurierroller:            'Kurierroller',
  lastendreirad:           'Lastendreirad',
  kleinwagen:              'Kleinwagen',
  geschaeftslimousine:     'Geschäftslimousine',
  gelaendewagen:           'Geländewagen',
  luxuslimousine:          'Luxuslimousine',
  gepanzerte_limousine:    'Gepanzerte Limousine',
  lieferwagen:             'Lieferwagen',
  kleintransporter:        'Kleintransporter',
  kuehltransporter:        'Kühltransporter',
  moebeltransporter:       'Möbeltransporter',
  gepanzerter_transporter: 'Gepanzerter Transporter',
  lkw_7_5_tonner:          '7,5-Tonner',
  schwerlast_lkw:          'Schwerlast-LKW',
  sattelzug:               'Sattelzug',
  // Pack-Items (Chris-Feedback 2026-07-30: fehlten in dieser Map, fielen auf den
  // Naive-Title-Case-Fallback zurück, der keine Umlaute kennt — "Blaettchen"/"Kapselhuelsen")
  blaettchen:           'Blättchen',
  kapselhuelsen:        'Kapselhülsen',
  zigarettenhuelsen:    'Zigarettenhülsen',
  // Fundgut (Job-Drops)
  schrott_metall:       'Metallschrott',
  schrott_plastik:      'Plastikschrott',
  schrott_elektronik:   'Elektronikschrott',
  pfandflasche:         'Pfandflasche',
  werkzeug_einfach:     'Werkzeug (einfach)',
  schmuggelware_pakete: 'Schmuggelware (Pakete)',
  schmuggelware_koffer: 'Schmuggelware (Koffer)',
  smartphone_guenstig:  'Smartphone (günstig)',
  smartphone_mittel:    'Smartphone (Mittelklasse)',
  smartphone_teuer:     'Smartphone (High-End)',
  laptop_alt:           'Laptop (gebraucht)',
  laptop_neu:           'Laptop (aktuell)',
  armbanduhr_einfach:   'Armbanduhr',
  armbanduhr_wertvoll:  'Armbanduhr (Marke)',
  schmuck_kette:        'Goldkette',
  schmuck_ring:         'Goldring',
  bargeld_buendel:      'Bargeld-Bündel',
  waffe_pistole:        'Pistole',
  // Phase-3 Loot-Items (NT-P3-LOOT-ITEMS-01, Job-Drops, nur Schwarzmarkt)
  kfz_ersatzteile:      'KFZ-Ersatzteile',
  gefaelschter_pass:    'Gefälschter Pass',
  blanko_dokument:      'Blanko-Dokument',
  blanko_ausweis:       'Blanko-Ausweis',
  laborrueckstand:      'Laborrückstand',
  // Materialien T1
  kupferkabel:        'Kupferkabel',
  autobatterie_alt:   'Autobatterie (alt)',
  eisenrohr:          'Eisenrohr (1m)',
  plastikkanister:    'Plastikkanister (5L)',
  schrauben_muttern:  'Schrauben & Muttern',
  glasscherben:       'Glasscherben (Sack)',
  elektronikschrott:  'Elektronikschrott',
  // Materialien T2
  aluminiumblech:     'Aluminiumblech',
  hydraulikschlauch:  'Hydraulikschlauch',
  stahlseil:          'Stahlseil',
  transformatorkern:  'Transformatorkern',
  industriepumpe:     'Industriepumpe (defekt)',
  // Materialien T3
  elektronik_bauteil:    'Elektronik-Bauteil',
  metallbauteil:         'Metallbauteil',
  praezisionsbauteil:    'Präzisionsbauteil',
  blaupausen_shard:      'Blaupausen-Shard',
  // Materialien - Werkstatt-Teile (NT-WERKSTATT-TEILE-01)
  kabelbaum:             'Kabelbaum',
  bremsbelaege:          'Bremsbeläge',
  auspufftopf:           'Auspufftopf',
  scheinwerfer:          'Scheinwerfer',
  getriebe:              'Getriebe',
  steuergeraet:          'Steuergerät',
  turbolader:            'Turbolader',
  motorblock:            'Motorblock',
  katalysator:           'Katalysator',
  // Labor-Zutaten
  isopropanol:           'Isopropanol',
  ethanol:               'Ethanol',
  aktivkohle:            'Aktivkohle',
  natriumcarbonat:       'Natriumcarbonat',
  salzsaeure:            'Salzsäure (verdünnt)',
  aceton:                'Aceton',
  destillieraufsatz:     'Destillieraufsatz',
  glaskolben_set:        'Glaskolben (Set)',
  ph_teststreifen:       'pH-Teststreifen',
  schutzhandschuhe:      'Schutzhandschuhe',
  plastikformen:         'Plastikformen',
  aromaole:              'Aromaöle',
  energydrink_basis:     'Energydrink-Basis',
  kapselmaschine:        'Kapselmaschine',
  methylamin:            'Methylamin',
  pseudoephedrin:        'Pseudoephedrin',
  phosphorpentachlorid:  'Phosphorpentachlorid',
  lithiumstreifen:       'Lithiumstreifen',
  synthetischer_precursor_x: 'Synthetischer Precursor X',
  katalysator_compound:  'Katalysator-Compound',
  aetznatron:            'Ätznatron',
  bindemittel_konzentrat: 'Bindemittel-Konzentrat',
  leuchtserum:           'Leuchtserum',
  adrenalin_konzentrat:  'Adrenalin-Konzentrat',
  // Garten-Equipment
  growzelt_klein:        'Growzelt (klein)',
  growzelt_gross:        'Growzelt (groß)',
  anzuchttopf:           'Anzuchttopf',
  hydroponik_topf:       'Hydroponik-Topf',
  naehrstoff_topf:       'Nährstoff-Topf',
  led_grow_lampe:        'LED-Grow-Lampe',
  profi_lampe:           'Profi-Lampe (Full Spectrum)',
  naehrloesung:          'Nährlösung',
  bew_automatisch:       'Auto-Bewässerung',
  co2_pad:               'CO2-Pad',
  aktivkohlefilter:      'Aktivkohlefilter',
  messgeraet_set:        'Messgerät-Set',
  bew_kanne:             'Bewässerungskanne',
  // Samen
  samen_kraeuter_mix:    'Kräuter-Mix Samen',
  samen_stadtgras:       'Stadtgras Samen',
  samen_nordlicht:       'Nordlicht Samen',
  samen_balkonkoenig:    'Balkonkönig Samen',
  samen_gruener_riese:   'Grüner Riese Samen',
  samen_purpurkrone:     'Purpurkrone Samen',
  samen_weisser_nebel:   'Weißer Nebel Samen',
  samen_kristallkind:    'Kristallkind Samen',
  samen_schwarzer_diamant: 'Schwarzer Diamant Samen',
  samen_schwarzwald_diesel: 'Schwarzwald-Diesel Samen',
  samen_silberfell:      'Silberfell Samen',
  // Labor-Produkte (Output)
  kristall_roh:          'Kristall (roh)',
  kristall_verpackt:     'Kristall (verpackt)',
  pulver_roh:            'Pulver (roh)',
  pulver_verpackt:       'Pulver (verpackt)',
  tabletten_roh:         'Tabletten (roh)',
  tabletten_verpackt:    'Tabletten (verpackt)',
  // Labor-Equipment (Markt-Items, neu)
  lab_stove:             'Herd (Labor)',
  lab_glassware:         'Glaskolben-Set',
  lab_protection:        'Schutzausrüstung',
  lab_distiller:         'Destille',
  lab_chemistry_set:     'Chemie-Set (Labor)',
  lab_capsule_machine:   'Kapselmaschine (Labor)',
  // Labor-Equipment (Legacy-Slot-Keys)
  herd_brenner:          'Herd & Brenner',
  schutzausruestung:     'Schutzausrüstung (alt)',
  chemi_set:             'Chemie-Set',
  endgame_setup:         'Endgame-Setup',
  // Garten-Equipment (ergänzt)
  growzelt_xl:           'Growzelt (Profi)',
  blacklight_x2:         'Blacklight X2',
  // Garten-Sonderitems
  trocknungsgestell:     'Trocknungsgestell',
  // Dokumente
  ausweis_basis:         'Gefälschter Ausweis (Basis)',
  ausweis_premium:       'Gefälschter Ausweis (Premium)',
  firmenpapiere:         'Firmenpapiere',
  lieferschein:          'Lieferschein (gefälscht)',
  zolldokument:          'Zolldokument (gefälscht)',
  mietvertrag:           'Mietvertrag (Scheinwohnung)',
  // Informationen
  polizei_schichtplan:   'Polizei-Schichtplan',
  razzia_zeitfenster:    'Razzia-Zeitfenster',
  undercover_warnung:    'Undercover-Warnung',
  markt_preisprognose:   'Markt-Preisprognose',
  schmuggel_route_intel: 'Schmuggel-Route-Intel',
  lieferanten_tipp:      'Lieferanten-Tipp',
};

/**
 * Wandelt einen Item-Key in einen lesbaren deutschen Namen um.
 * Beispiel: "pkw_alt" → "PKW (alt)"
 */
export function formatItemKey(key: string): string {
  if (!key) return key;
  // Qualitäts-/Varianten-tragende Items (Joints, Blunts, Tabak-Zwischenprodukte):
  // Qualität/Variante gehören ins Badge, nicht in den Namen — siehe item-badges.ts.
  if (extractQualityTier(key) !== null) return getCleanItemName(key);
  // Labor-Output-Keys (output_X, output_X__pulversorte): Namen kommen aus LABOR_OUTPUT_LABELS
  if (key.startsWith('output_')) return getCleanItemName(key);
  const lower = key.toLowerCase();
  const mapped = ITEM_KEY_MAP[lower];
  if (mapped !== undefined) return mapped;
  // Fallback: snake_case → Title Case
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
